import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, Form, Alert, Spinner,
} from 'reactstrap';
import {
  getEmployeeById, updateEmployee,
  getCompanyById, updateCompany,
  getOfferById, updateOffer,
  updateUser, 
  adminUploadImage, adminWriteImage, adminDeleteImg
} from '../../../../services/userservice';
import { FieldInput } from './FieldInput';
import { DocumentSection } from './DocumentSection';
import { UsuarioForm } from './UsuarioForm';
import { toast } from 'react-toastify';
// ── Configuración ────────────────────────────────────────────────────────────

const SERVICE_MAP = {
  candidato: { get: getEmployeeById, update: updateEmployee },
  empresa:   { get: getCompanyById,  update: updateCompany },
  oferta:    { get: getOfferById,    update: updateOffer },
};

const TYPE_KEY_MAP = {
  candidato: 'employee',
  empresa:   'company',
  oferta:    'offer',
};

export const SKIP_FIELDS = new Set([
  'id', 'user_id', 'category_id', 'company_id',
  'created_at', 'updated_at', 'createdAt', 'updatedAt',
]);

const READ_ONLY_FIELDS = new Set(['category.category_name', 'category.description', 'saved_count', 'application_count',
                                  'active_offers', 'next_reset_date'
]);

const FIELD_TRANSLATIONS = {
  // Comunes
  name: 'Nombre',
  first_name: 'Nombre',
  lastname: 'Apellido',
  last_name: 'Apellido',
  email: 'Correo Electrónico',
  phone: 'Teléfono',
  address: 'Dirección',
  description: 'Descripción',
  photo: 'Foto de Perfil',
  status: 'Estado',
  country: 'Pais',
  department: 'Departamento',
  adress: 'Dirección',
  identificationNumber: 'Numero de identificacion',
  typeIdentification: 'Tipo de identificacion',
  nationality: 'Nacionalidad',
  neighborhood: 'Barrio',
  salary: 'Salario',
  
  // Candidato / Empleado
  birth_date: 'Fecha de Nacimiento',
  experience_years: 'Años de Experiencia',
  education: 'Educación',
  skills: 'Habilidades',
  city: 'Ciudad',
  linkedin_url: 'Perfil de LinkedIn',
  resume: 'Currículum',
  studyLevel: 'Nivel de estudio',
  birthDate: 'Fecha de nacimiento',
  languaje: 'Lenguaje',
  first_job: 'Primer Trabajo',
  
  // Empresa
  company_name: 'Nombre de la Empresa',
  active_offers: 'Ofertas Activas',
  active_offers_limit: 'Limite de ofertas activas',
  available_credits: 'Creditos disponibles',
  check_digit: 'Dígito de verificación',
  contact_name: 'Nombre de contacto',
  contact_number: 'Numero del contacto',
  contact_position: 'Cargo del contacto dentro de la empresa',
  monthly_credit_limit: 'Limite de creditos mensuales',
  next_reset_date: 'Proxima restauracion de creditos',
  verified: 'Empresa verificada',
  
  // Oferta
  job_offer: 'Oferta de trabajo',
  'job_offer.title': 'Título de la vacante',
  'job_offer.salary': 'Salario',
  'job_offer.description': 'Descripcion de la vacante',
  'job_offer.modality': 'Modalidad',
  'job_offer.date': 'Fecha',
  'job_offer.experience': 'Experiencia requerida',
  'job_offer.city': 'Ciudad',
  'job_offer.department': 'Departamento',
  'job_offer.languaje': 'Lenguaje',
  'job_offer.contract_type': 'Tipo de contrato',
  'job_offer.languaje_level': 'Dominio del lenguaje',
  'job_offer.required_vehicle': 'Necesita vehiculo?',
  'job_offer.study_level': 'Nivel de estudio',
  'job_offer.suspended': 'Suspendida',
  'job_offer.available_time': 'Tiempo disponible',
  'job_offer.status': 'Estado',  

  // Categoria de oferta
  category: 'Categoria',
  'category.category_name': 'Nombre de categoria',
  'category.description': 'Descripcion de categoria',
  
  // Relacionados a oferta
  technology_tools: 'Herramientas requeridas',
  saved_count: 'Cantidad de guardados',
  application_count: 'Cantidad de aplicantes',
  
  // Otros
  username: 'Nombre de Usuario',
  role_id: 'ID de Rol',
  banned: 'Baneado / Suspendido',
  password: 'Contraseña',
  confirmPassword: 'Confirmar Contraseña',
};

// ── Utilidades ───────────────────────────────────────────────────────────────

export const extractData = (response, type) => {
  const raw = response?.data ?? response;
  const entityKey = TYPE_KEY_MAP[type];

  const fields = (entityKey && raw?.[entityKey]) ? raw[entityKey] : (raw ?? {});
  const documents = Array.isArray(raw?.documents) ? raw.documents : [];

  return { fields, documents };
};

export const getInputType = (key, value) => {
  if (typeof value === 'boolean') return 'checkbox';
  // IMPORTANTE: Buscamos en la llave original (ej: 'email', 'phone')
  if (key.includes('email')) return 'email';
  if (key === 'photo') return 'url';
  if (key.includes('phone') || key.includes('telefono')) return 'tel';
  if (key.includes('date') || key.includes('Date') || key.includes('fecha')) return 'date';
  if (key.includes('description') || key.includes('descripcion') || key.includes('Description')) {
    return 'textarea';
  }
  return 'text';
};

export const formatLabel = (key) => {
  // 1. Si la llave existe en el diccionario, la usamos
  if (FIELD_TRANSLATIONS[key]) {
    return FIELD_TRANSLATIONS[key];
  }

  // 2. Si no existe, usamos tu lógica original de formateo
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
};

const setNestedValue = (obj, path, value) => {
  const keys = path.split('.');
  const lastKey = keys.pop();
  const lastObj = keys.reduce((target, key) => (target[key] = { ...target[key] }), obj);
  lastObj[lastKey] = value;
  return { ...obj };
};

// ── Componente principal ─────────────────────────────────────────────────────

const ModalDetalles = ({ isOpen, toggle, itemId, type, userData, onSuccess }) => {
  const [fields, setFields] = useState({});
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  const fileInputRef = useRef(null);
  const [currentPhotoData, setCurrentPhotoData] = useState({ fieldKey: '', oldFileName: '' });

  const isUsuario = type === 'usuario';

  const handleRequestImageUpdate = (fieldKey, oldFileName) => {
    setCurrentPhotoData({ fieldKey, oldFileName });
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const userId = fields.user_id || userData?.id;
    const entityId = fields.id;

    if (!userId) {
      toast.error("No se pudo encontrar el ID de usuario para la carga.");
      return;
    }

    setUpdating(true);
    try {
      if (currentPhotoData.oldFileName) {
        await adminDeleteImg(currentPhotoData.oldFileName);
      }

      const response = await adminUploadImage(userId, file);
        
      const newUrl = 'https://storage.googleapis.com/avanza-files' + response.url; 
      handleInputChange({
        target: { name: currentPhotoData.fieldKey, value: newUrl }
      });

      await adminWriteImage(entityId, type, response.filename)

      toast.success("Imagen actualizada con éxito");
      
    } catch (err) {
      console.error("Error al procesar la imagen:", err);
      toast.error("Error al subir la imagen");
    } finally {
      setUpdating(false);
      e.target.value = null;
    }
  };

  const reloadData = useCallback(async () => {
    if (!itemId || isUsuario) return;
    
    setLoading(true);
    try {
      const service = SERVICE_MAP[type];
      if (service) {
        const response = await service.get(itemId);
        const { fields: f, documents: d } = extractData(response, type);
        setFields(f ?? {});
        setDocuments(d);
      }
    } catch (err) {
      console.error('Error recargando datos:', err);
      setError('Error al recargar los datos.');
    } finally {
      setLoading(false);
    }
  }, [itemId, type, isUsuario]);
  
  // Cargar datos al abrir el modal
  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen || !itemId) return;
      setLoading(true);
      setError(null);

      try {
        if (isUsuario) {
          setFields({
            id: userData?.id ?? '',
            username: userData?.username ?? '',
            email: userData?.email ?? '',
            role_id: userData?.role_id ?? '',
            banned: userData?.banned ?? false,
            password: '',
            confirmPassword: '',
          });
          setDocuments([]);
        } else {
          await reloadData();
        }
      } catch (err) {
        console.error('Error cargando datos:', err);
        setError('No se pudieron cargar los datos. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isOpen, itemId, type, userData, isUsuario, reloadData]);

  // Limpiar estado al cerrar
  useEffect(() => {
    if (!isOpen) {
      setFields({});
      setDocuments([]);
      setError(null);
      setUpdating(false);
    }
  }, [isOpen]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      setFields((prev) => setNestedValue({ ...prev }, name, value));
    } else {
      setFields((prev) => ({ ...prev, [name]: value }));
    }
  }, []);

  const handleDocumentUploaded = useCallback(async (filename) => {
    // Recargar datos para mostrar el nuevo documento
    await reloadData();
  }, [reloadData]);

  const handleDocumentDeleted = useCallback(async () => {
    // Recargar datos para quitar el documento eliminado de la lista
    await reloadData();
  }, [reloadData]);

  const validateUsuarioData = () => {
    if (fields.password && fields.password !== fields.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return false;
    }
    return true;
  };

  const buildUpdatePayload = () => {
    if (isUsuario) {
      const updateData = {};
      if (fields.password) updateData.password = fields.password;
      if (fields.banned !== undefined) {
        updateData.banned = fields.banned;
      }
      return updateData;
    }

    // Solo campos editables: excluir ids, timestamps, etc.
    return Object.fromEntries(
      Object.entries(fields).filter(([key]) => !SKIP_FIELDS.has(key))
    );
  };

  const handleUpdate = async () => {
    if (isUsuario && !validateUsuarioData()) return;

    setUpdating(true);
    setError(null);

    try {
      const payload = buildUpdatePayload();

      if (Object.keys(payload).length === 0) {
        toggle();
        return;
      }

      if (isUsuario) {
        await updateUser(itemId, payload);
      } else {
        const service = SERVICE_MAP[type];
        if (!service) throw new Error(`Tipo desconocido: ${type}`);
        await service.update(itemId, payload);
      }
      toast.success('Datos actualizados correctamente');
      if (onSuccess) {
          onSuccess(itemId, payload);
        }
      toggle();
    } catch (err) {
      console.error('Error al actualizar:', err);
      const serverMessage = err.response?.data?.detail 
        || err.response?.data?.message 
        || err.message 
        || 'Error desconocido';

      toast.error(`Error: ${serverMessage}`);
      setError('Error al actualizar los datos. Por favor, intente nuevamente.');
    } finally {
      setUpdating(false);
    }
  };

  const renderFields = (data, prefix = '') => {
    return Object.entries(data)
      .filter(([key]) => !SKIP_FIELDS.has(key))
      .map(([key, value]) => {
        const fullKey = prefix ? `${prefix}.${key}` : key;

        // Si es un objeto, pero NO un array ni nulo, profundizar
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          return (
            <div key={fullKey} className="ms-3 border-start ps-3 mb-4">
              <h6 className="text-primary mt-2">{formatLabel(key)}</h6>
              {renderFields(value, fullKey)}
            </div>
          );
        }

        if (Array.isArray(value)) {
          return (
            <div key={fullKey} className="mb-3">
              <span className="fw-bold">{formatLabel(key)}: </span>
              <span className="text-muted">
                {value.length > 0 
                  ? value.map(tool => tool.tool_name).join(', ') 
                  : 'Ninguna'}
              </span>
            </div>
          );
        }

        return (
          <FieldInput
            key={fullKey}
            fieldKey={fullKey}
            displayLabel={key}
            value={value}
            onChange={handleInputChange}
            disabled={READ_ONLY_FIELDS.has(fullKey)} 
            onUpdateImage={handleRequestImageUpdate}
          />
        );
      });
  };

  const renderContent = () => {
    if (loading) return <div className="text-center py-5"><Spinner color="dark" /></div>;
    if (error && !updating) return <Alert color="danger">{error}</Alert>;

    if (isUsuario) return <UsuarioForm fields={fields} onChange={handleInputChange} />;

    return (
      <>
        {renderFields(fields)}

        {type !== 'oferta' && (
        <DocumentSection
          documents={documents}
          onDocumentUploaded={handleDocumentUploaded}
          onDocumentDeleted={handleDocumentDeleted}
          userId={fields.user_id}
          entityId={itemId}
          entityType={type}
        />
      )}
      </>
    );
  };

  const modalTitle = {
    candidato: 'Editar Candidato',
    empresa: 'Editar Empresa',
    oferta: 'Editar Oferta',
    usuario: 'Editar Usuario',
  }[type] ?? 'Editar';

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable zIndex={9999}>
      <ModalHeader toggle={toggle}>{modalTitle}</ModalHeader>
      <ModalBody>
        <Form onSubmit={(e) => e.preventDefault()}>
          {renderContent()}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            onChange={handleImageChange}
          />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={updating}>
          Cancelar
        </Button>
        <Button 
          color="dark" 
          onClick={handleUpdate} 
          disabled={loading || updating}
        >
          {updating ? <Spinner size="sm" /> : 'Actualizar'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ModalDetalles;